import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Car, Bike, Droplets, CircleDot, Shield, MapPin, Phone, 
  ChevronRight, Check, Home, Building2, Clock, Star,
  Wrench, Award, Crown, Gift, CreditCard, X, CheckCircle
} from 'lucide-react';
import SystemPayForm from '../components/SystemPayForm';
import { redirectToSystemPay } from '../lib/systempayRedirect';

const PAYMENTS_ENABLED = process.env.REACT_APP_PAYMENTS_ENABLED !== 'false';
const SUBSCRIBE_LABEL = PAYMENTS_ENABLED ? 'Acheter en ligne' : 'Reserver (paiement en magasin)';

// Prestations lavage à domicile (+10€ Petite Couronne / +20€ Grande Couronne)
const LAVAGE_DOMICILE = [
  {
    id: 'clean-domicile',
    name: 'CLEAN',
    priceCenter: 25,
    pricePetiteCouronne: 35,
    priceGrandeCouronne: 45,
    features: ['Prélavage', 'Lavage carrosserie', 'Jantes', 'Brillant pneus'],
    icon: Droplets,
    color: 'text-blue-400',
    vehicleTypes: ['auto', 'moto']
  },
  {
    id: 'vip-domicile',
    name: 'VIP',
    priceCenter: 55,
    pricePetiteCouronne: 65,
    priceGrandeCouronne: 75,
    features: ['Prélavage', 'Lavage carrosserie', 'Jantes', 'Brillant pneus', 'Essuyage intérieur porte', 'Vitres', 'Essuyage plastiques', 'Aspiration', 'Parfum'],
    icon: Star,
    color: 'text-yellow-400',
    popular: true,
    vehicleTypes: ['auto']
  },
  {
    id: 'platine-domicile',
    name: 'PLATINE',
    priceCenter: 75,
    pricePetiteCouronne: 85,
    priceGrandeCouronne: 95,
    features: ['Prélavage', 'Lavage carrosserie', 'Jantes', 'Brillant pneus', 'Essuyage intérieur porte', 'Vitres', 'Essuyage plastiques', 'Aspiration', 'Parfum', 'Shampoing tapis'],
    icon: Crown,
    color: 'text-purple-400',
    vehicleTypes: ['auto']
  },
  {
    id: 'prestige-domicile',
    name: 'PRESTIGE',
    priceCenter: 115,
    pricePetiteCouronne: 125,
    priceGrandeCouronne: 135,
    features: ['Prélavage', 'Lavage carrosserie', 'Jantes', 'Brillant pneus', 'Essuyage intérieur porte', 'Vitres', 'Essuyage plastiques', 'Aspiration', 'Parfum', 'Shampoing tapis', 'Plastiques nourris et ravivés', 'Cire brillant professionnel', 'Protection contre UV, pluie et saletés'],
    icon: Award,
    color: 'text-red-500',
    vehicleTypes: ['auto']
  },
];

// Services pneumatique à domicile
const SERVICES_PNEU = [
  {
    id: 'crevaison',
    name: 'Réparation Crevaison',
    description: 'Réparation de pneu crevé sur place',
    price: 'Sur devis',
    icon: CircleDot
  },
  {
    id: 'changement-pneu',
    name: 'Changement de Pneu',
    description: 'Remplacement de pneu(s) à domicile ou au bureau',
    price: 'Sur devis',
    icon: Wrench
  },
  {
    id: 'permutation',
    name: 'Permutation Pneus',
    description: 'Permutation des pneus avant/arrière',
    price: 'Sur devis',
    icon: CircleDot
  },
  {
    id: 'pression',
    name: 'Contrôle & Gonflage',
    description: 'Vérification pression et gonflage des pneus',
    price: 'Sur devis',
    icon: CircleDot
  },
];

// Services vitrage à domicile
const SERVICES_VITRAGE = [
  {
    id: 'impact',
    name: 'Réparation Impact',
    description: 'Réparation d\'impact sur pare-brise (évite le remplacement)',
    price: 'Sur devis',
    icon: Shield
  },
  {
    id: 'remplacement-parebrise',
    name: 'Remplacement Pare-brise',
    description: 'Changement complet du pare-brise à domicile. Franchise offerte jusqu\'à 130€ !',
    price: 'Sur devis',
    icon: Shield
  },
  {
    id: 'vitre-laterale',
    name: 'Vitre Latérale',
    description: 'Remplacement vitre latérale cassée ou endommagée',
    price: 'Sur devis',
    icon: Shield
  },
  {
    id: 'lunette-arriere',
    name: 'Lunette Arrière',
    description: 'Remplacement lunette arrière',
    price: 'Sur devis',
    icon: Shield
  },
];

// Avantage vitrage
const VITRAGE_AVANTAGE = {
  title: "Franchise offerte jusqu'à 130€",
  subtitle: "Nous prenons en charge toutes vos démarches administratives auprès de votre assureur"
};

export default function MobilitePage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [selectedZone, setSelectedZone] = useState('petiteCouronne');
  const [paymentData, setPaymentData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const paymentSectionRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const API = API_URL + '/api';

  useEffect(() => {
    if (!paymentData) return;
    setTimeout(() => {
      paymentSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  }, [paymentData]);

  const handlePaid = useCallback(async () => {
    if (!paymentData) return;
    try {
      await fetch(`${API}/subscriptions/confirm?subscription_id=${paymentData.subscriptionId}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) { console.error(e); }
    setPaymentStatus('success');
    toast.success('Abonnement active !');
  }, [paymentData, API, token]);

  const handleFailed = useCallback(() => setPaymentStatus('error'), []);

  const cancelPayment = () => {
    setPaymentData(null);
    setPaymentStatus(null);
  };

  const handleBuySubscription = async (planId, planName, price, zone) => {
    if (!user) {
      toast.error('Veuillez vous connecter pour acheter');
      navigate('/connexion');
      return;
    }

    setLoadingPlan(planId);
    const fullPlanName = `${planName} - ${zone === 'petiteCouronne' ? 'Petite Couronne' : 'Grande Couronne'}`;
    try {
      const res = await fetch(`${API_URL}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          plan_id: planId,
          plan_name: fullPlanName,
          price: price,
          origin_url: window.location.origin,
          subscription_type: 'mobilite',
          zone: zone
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erreur lors de la création du paiement');
      }

      const data = await res.json();
      if (data.skip_payment) {
        setPaymentData({ formToken: null, subscriptionId: data.subscriptionId, planName: fullPlanName, price });
        setPaymentStatus('success');
        toast.success('Demande enregistree ! A regler en magasin.');
      } else if (data.redirect_form) {
        redirectToSystemPay(data.redirect_form);
      } else if (data.formToken) {
        setPaymentData({
          formToken: data.formToken,
          subscriptionId: data.subscriptionId,
          planName: fullPlanName,
          price
        });
      } else {
        toast.error('Erreur lors de la création du paiement');
      }
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la création du paiement');
      console.error(error);
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Hero */}
      <section className="bg-gradient-to-b from-zinc-900 to-[#09090B] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-600/20 text-orange-400 px-4 py-2 rounded-full border border-orange-600/30 mb-6">
            <Car size={18} />
            <span className="text-sm font-medium">Service à domicile</span>
          </div>
          <h1 className="text-5xl font-bold text-white mb-4">
            SERVICE <span className="text-red-500">MOBILITÉ</span>
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-6">
            Nous venons à vous ! Lavage, pneumatique et vitrage directement à votre domicile ou au bureau.
          </p>
          <div className="flex justify-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 bg-zinc-800/50 px-4 py-2 rounded-full">
              <Home className="text-orange-400" size={20} />
              <span className="text-white">À domicile</span>
            </div>
            <div className="flex items-center gap-2 bg-zinc-800/50 px-4 py-2 rounded-full">
              <Building2 className="text-orange-400" size={20} />
              <span className="text-white">Au bureau</span>
            </div>
            <div className="flex items-center gap-2 bg-zinc-800/50 px-4 py-2 rounded-full">
              <MapPin className="text-orange-400" size={20} />
              <span className="text-white">Paris & Île-de-France</span>
            </div>
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-8 bg-gradient-to-r from-orange-900/20 via-orange-600/10 to-orange-900/20 border-y border-orange-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-6 text-center">
            <div>
              <Clock className="mx-auto text-orange-400 mb-2" size={28} />
              <p className="text-white font-semibold">Gain de temps</p>
              <p className="text-zinc-500 text-sm">On s'occupe de tout</p>
            </div>
            <div>
              <Home className="mx-auto text-orange-400 mb-2" size={28} />
              <p className="text-white font-semibold">Confort</p>
              <p className="text-zinc-500 text-sm">Sans vous déplacer</p>
            </div>
            <div>
              <Star className="mx-auto text-orange-400 mb-2" size={28} />
              <p className="text-white font-semibold">Même qualité</p>
              <p className="text-zinc-500 text-sm">Service professionnel</p>
            </div>
            <div>
              <MapPin className="mx-auto text-orange-400 mb-2" size={28} />
              <p className="text-white font-semibold">Large zone</p>
              <p className="text-zinc-500 text-sm">Paris & banlieue</p>
            </div>
          </div>
        </div>
      </section>

      {/* Lavage à domicile */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-600/20 text-blue-400 px-4 py-2 rounded-full border border-blue-600/30 mb-4">
              <Droplets size={18} />
              <span className="text-sm font-medium">Lavage à domicile</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">LAVAGE AUTO À DOMICILE</h2>
            <p className="text-zinc-400 mb-4">Les mêmes prestations qu'au centre avec frais de déplacement</p>
            <div className="flex justify-center gap-4 flex-wrap">
              <span className="bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm border border-green-500/30">
                Paris & Petite Couronne : +10€
              </span>
              <span className="bg-orange-500/20 text-orange-400 px-4 py-2 rounded-full text-sm border border-orange-500/30">
                Grande Couronne : +20€
              </span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {LAVAGE_DOMICILE.map((service, idx) => (
              <Card 
                key={service.id}
                className={`bg-zinc-900/50 border-zinc-800 overflow-hidden card-hover ${
                  service.popular ? 'border-yellow-500/50 ring-1 ring-yellow-500/30' : ''
                }`}
                data-testid={`service-${service.id}`}
              >
                {service.popular && (
                  <div className="bg-yellow-500 text-black text-center py-1 text-xs font-bold">
                    POPULAIRE
                  </div>
                )}
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl bg-zinc-800 flex items-center justify-center`}>
                      <service.icon className={service.color} size={24} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">{service.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {service.vehicleTypes?.includes('auto') && (
                          <div className="flex items-center gap-1 bg-zinc-800 px-2 py-0.5 rounded-full">
                            <Car size={12} className="text-red-500" />
                            <span className="text-xs text-zinc-400">Auto</span>
                          </div>
                        )}
                        {service.vehicleTypes?.includes('moto') && (
                          <div className="flex items-center gap-1 bg-zinc-800 px-2 py-0.5 rounded-full">
                            <Bike size={12} className="text-red-500" />
                            <span className="text-xs text-zinc-400">Moto</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-4 space-y-2">
                    <div className="flex items-center justify-between bg-green-500/10 rounded-lg px-3 py-2">
                      <span className="text-green-400 text-sm">Petite Couronne</span>
                      <span className="text-white font-bold">{service.pricePetiteCouronne}€</span>
                    </div>
                    <div className="flex items-center justify-between bg-orange-500/10 rounded-lg px-3 py-2">
                      <span className="text-orange-400 text-sm">Grande Couronne</span>
                      <span className="text-white font-bold">{service.priceGrandeCouronne}€</span>
                    </div>
                    <p className="text-zinc-500 text-xs text-center">Au centre : {service.priceCenter}€</p>
                  </div>

                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-zinc-300 text-sm">
                        <Check className="text-green-500 flex-shrink-0" size={14} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link to="/reservation-mobilite">
                    <Button className={`w-full ${service.popular ? 'bg-yellow-500 hover:bg-yellow-600 text-black' : 'bg-red-600 hover:bg-red-700'}`}>
                      Réserver
                      <ChevronRight size={18} className="ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Section Abonnement Mobilité */}
          <section id="abonnements-mobilite" className="mt-16 py-12 bg-gradient-to-b from-orange-900/20 to-zinc-900/50 rounded-2xl border border-orange-500/30">
            <div className="px-6">
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-green-600/20 text-green-400 px-4 py-2 rounded-full border border-green-600/30 mb-4">
                  <Gift size={18} />
                  <span className="text-sm font-medium">Offre Fidélité Mobilité</span>
                </div>
                <h2 className="text-3xl font-bold text-white mb-2">
                  FORMULES <span className="text-green-400">ABONNEMENT MOBILITÉ</span>
                </h2>
                <p className="text-xl text-zinc-400 mb-4">10 lavages à domicile achetés = 2 OFFERTS</p>
                
                {/* Zone Selector */}
                <div className="flex justify-center gap-4 mt-6">
                  <Button
                    onClick={() => setSelectedZone('petiteCouronne')}
                    className={`${selectedZone === 'petiteCouronne' ? 'bg-green-600 hover:bg-green-700' : 'bg-zinc-800 hover:bg-zinc-700'}`}
                  >
                    <MapPin size={18} className="mr-2" />
                    Petite Couronne (+10€)
                  </Button>
                  <Button
                    onClick={() => setSelectedZone('grandeCouronne')}
                    className={`${selectedZone === 'grandeCouronne' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-zinc-800 hover:bg-zinc-700'}`}
                  >
                    <MapPin size={18} className="mr-2" />
                    Grande Couronne (+20€)
                  </Button>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* CLEAN Abonnement Mobilité */}
                <Card className="bg-zinc-900/80 border-zinc-800 border-blue-500/30 overflow-hidden">
                  <div className="bg-blue-600 text-white text-center py-2 text-sm font-bold">
                    2 LAVAGES OFFERTS
                  </div>
                  <CardContent className="p-6 text-center">
                    <Droplets className="text-blue-400 mx-auto mb-3" size={32} />
                    <h3 className="text-xl font-bold text-white mb-2">CLEAN x12</h3>
                    <p className="text-zinc-500 text-sm mb-4">Carte 12 lavages CLEAN à domicile</p>
                    <div className="mb-4">
                      <span className="text-zinc-500 line-through text-lg">
                        {selectedZone === 'petiteCouronne' ? '420€' : '540€'}
                      </span>
                      <p className="text-4xl font-bold text-blue-400">
                        {selectedZone === 'petiteCouronne' ? '350€' : '450€'}
                      </p>
                      <span className="text-zinc-500 text-sm">
                        soit {selectedZone === 'petiteCouronne' ? '29.17' : '37.50'}€/lavage
                      </span>
                    </div>
                    <div className="bg-blue-500/10 rounded-lg p-3 mb-4">
                      <p className="text-blue-400 font-semibold">
                        Économie : {selectedZone === 'petiteCouronne' ? '70€' : '90€'}
                      </p>
                    </div>
                    <Button 
                      onClick={() => handleBuySubscription(
                        'abo-clean-mobile', 
                        'CLEAN x12 Mobilité', 
                        selectedZone === 'petiteCouronne' ? 350 : 450,
                        selectedZone
                      )}
                      disabled={loadingPlan === 'abo-clean-mobile'}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6 font-bold"
                      data-testid="buy-abo-clean-mobile"
                    >
                      <CreditCard size={20} className="mr-2" />
                      {loadingPlan === 'abo-clean-mobile' ? 'Chargement...' : SUBSCRIBE_LABEL}
                    </Button>
                  </CardContent>
                </Card>

                {/* VIP Abonnement Mobilité */}
                <Card className="bg-zinc-900/80 border-yellow-500/50 ring-2 ring-yellow-500/30 overflow-hidden">
                  <div className="bg-yellow-500 text-black text-center py-2 text-sm font-bold">
                    ⭐ BEST SELLER - 2 OFFERTS
                  </div>
                  <CardContent className="p-6 text-center">
                    <Star className="text-yellow-400 mx-auto mb-3" size={32} />
                    <h3 className="text-xl font-bold text-white mb-2">VIP x12</h3>
                    <p className="text-zinc-500 text-sm mb-4">Carte 12 lavages VIP à domicile</p>
                    <div className="mb-4">
                      <span className="text-zinc-500 line-through text-lg">
                        {selectedZone === 'petiteCouronne' ? '780€' : '900€'}
                      </span>
                      <p className="text-4xl font-bold text-yellow-400">
                        {selectedZone === 'petiteCouronne' ? '650€' : '750€'}
                      </p>
                      <span className="text-zinc-500 text-sm">
                        soit {selectedZone === 'petiteCouronne' ? '54.17' : '62.50'}€/lavage
                      </span>
                    </div>
                    <div className="bg-yellow-500/10 rounded-lg p-3 mb-4">
                      <p className="text-yellow-400 font-semibold">
                        Économie : {selectedZone === 'petiteCouronne' ? '130€' : '150€'}
                      </p>
                    </div>
                    <Button 
                      onClick={() => handleBuySubscription(
                        'abo-vip-mobile', 
                        'VIP x12 Mobilité', 
                        selectedZone === 'petiteCouronne' ? 650 : 750,
                        selectedZone
                      )}
                      disabled={loadingPlan === 'abo-vip-mobile'}
                      className="w-full bg-yellow-500 hover:bg-yellow-600 text-black text-lg py-6 font-bold"
                      data-testid="buy-abo-vip-mobile"
                    >
                      <CreditCard size={20} className="mr-2" />
                      {loadingPlan === 'abo-vip-mobile' ? 'Chargement...' : SUBSCRIBE_LABEL}
                    </Button>
                  </CardContent>
                </Card>

                {/* PLATINE Abonnement Mobilité */}
                <Card className="bg-zinc-900/80 border-zinc-800 border-purple-500/30 overflow-hidden">
                  <div className="bg-purple-600 text-white text-center py-2 text-sm font-bold">
                    2 LAVAGES OFFERTS
                  </div>
                  <CardContent className="p-6 text-center">
                    <Crown className="text-purple-400 mx-auto mb-3" size={32} />
                    <h3 className="text-xl font-bold text-white mb-2">PLATINE x12</h3>
                    <p className="text-zinc-500 text-sm mb-4">Carte 12 lavages PLATINE à domicile</p>
                    <div className="mb-4">
                      <span className="text-zinc-500 line-through text-lg">
                        {selectedZone === 'petiteCouronne' ? '1020€' : '1140€'}
                      </span>
                      <p className="text-4xl font-bold text-purple-400">
                        {selectedZone === 'petiteCouronne' ? '850€' : '950€'}
                      </p>
                      <span className="text-zinc-500 text-sm">
                        soit {selectedZone === 'petiteCouronne' ? '70.83' : '79.17'}€/lavage
                      </span>
                    </div>
                    <div className="bg-purple-500/10 rounded-lg p-3 mb-4">
                      <p className="text-purple-400 font-semibold">
                        Économie : {selectedZone === 'petiteCouronne' ? '170€' : '190€'}
                      </p>
                    </div>
                    <Button 
                      onClick={() => handleBuySubscription(
                        'abo-platine-mobile', 
                        'PLATINE x12 Mobilité', 
                        selectedZone === 'petiteCouronne' ? 850 : 950,
                        selectedZone
                      )}
                      disabled={loadingPlan === 'abo-platine-mobile'}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-lg py-6 font-bold"
                      data-testid="buy-abo-platine-mobile"
                    >
                      <CreditCard size={20} className="mr-2" />
                      {loadingPlan === 'abo-platine-mobile' ? 'Chargement...' : SUBSCRIBE_LABEL}
                    </Button>
                  </CardContent>
                </Card>

                {/* PRESTIGE Abonnement Mobilité */}
                <Card className="bg-zinc-900/80 border-zinc-800 border-red-500/30 overflow-hidden">
                  <div className="bg-red-600 text-white text-center py-2 text-sm font-bold">
                    2 LAVAGES OFFERTS
                  </div>
                  <CardContent className="p-6 text-center">
                    <Award className="text-red-500 mx-auto mb-3" size={32} />
                    <h3 className="text-xl font-bold text-white mb-2">PRESTIGE x12</h3>
                    <p className="text-zinc-500 text-sm mb-4">Carte 12 lavages PRESTIGE à domicile</p>
                    <div className="mb-4">
                      <span className="text-zinc-500 line-through text-lg">
                        {selectedZone === 'petiteCouronne' ? '1500€' : '1620€'}
                      </span>
                      <p className="text-4xl font-bold text-red-500">
                        {selectedZone === 'petiteCouronne' ? '1250€' : '1350€'}
                      </p>
                      <span className="text-zinc-500 text-sm">
                        soit {selectedZone === 'petiteCouronne' ? '104.17' : '112.50'}€/lavage
                      </span>
                    </div>
                    <div className="bg-red-500/10 rounded-lg p-3 mb-4">
                      <p className="text-red-500 font-semibold">
                        Économie : {selectedZone === 'petiteCouronne' ? '250€' : '270€'}
                      </p>
                    </div>
                    <Button 
                      onClick={() => handleBuySubscription(
                        'abo-prestige-mobile', 
                        'PRESTIGE x12 Mobilité', 
                        selectedZone === 'petiteCouronne' ? 1250 : 1350,
                        selectedZone
                      )}
                      disabled={loadingPlan === 'abo-prestige-mobile'}
                      className="w-full bg-red-600 hover:bg-red-700 text-lg py-6 font-bold"
                      data-testid="buy-abo-prestige-mobile"
                    >
                      <CreditCard size={20} className="mr-2" />
                      {loadingPlan === 'abo-prestige-mobile' ? 'Chargement...' : SUBSCRIBE_LABEL}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-8 text-center">
                <p className="text-zinc-400">
                  <Check className="inline text-green-500 mr-2" size={18} />
                  Paiement sécurisé par Stripe • Carte valable 12 mois • Utilisable à domicile ou au bureau
                </p>
              </div>
            </div>
          </section>
        </div>
      </section>

      {/* Pneumatique à domicile */}
      <section className="py-16 bg-zinc-900/30 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-zinc-600/20 text-zinc-300 px-4 py-2 rounded-full border border-zinc-600/30 mb-4">
              <CircleDot size={18} />
              <span className="text-sm font-medium">Pneumatique à domicile</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">RÉPARATION & CHANGEMENT PNEUS</h2>
            <p className="text-zinc-400">Service pneumatique mobile à domicile ou au bureau</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES_PNEU.map((service, idx) => (
              <Card 
                key={service.id}
                className="bg-zinc-900/50 border-zinc-800 overflow-hidden card-hover"
                data-testid={`pneu-${service.id}`}
              >
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
                    <service.icon className="text-zinc-300" size={28} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{service.name}</h3>
                  <p className="text-zinc-400 text-sm mb-4">{service.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-red-500 font-bold">{service.price}</span>
                    <Link to="/devis">
                      <Button size="sm" variant="outline" className="border-zinc-700 text-white hover:bg-zinc-800">
                        Devis
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-zinc-500 mb-4">Nous intervenons avec tout le matériel nécessaire directement sur place</p>
            <div className="flex justify-center gap-4 flex-wrap">
              <span className="bg-zinc-800 text-zinc-300 px-4 py-2 rounded-full text-sm">✓ Équilibrage</span>
              <span className="bg-zinc-800 text-zinc-300 px-4 py-2 rounded-full text-sm">✓ Montage/Démontage</span>
              <span className="bg-zinc-800 text-zinc-300 px-4 py-2 rounded-full text-sm">✓ Réparation</span>
              <span className="bg-zinc-800 text-zinc-300 px-4 py-2 rounded-full text-sm">✓ Remplacement</span>
            </div>
          </div>
        </div>
      </section>

      {/* Vitrage à domicile */}
      <section className="py-16 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-cyan-600/20 text-cyan-400 px-4 py-2 rounded-full border border-cyan-600/30 mb-4">
              <Shield size={18} />
              <span className="text-sm font-medium">Vitrage à domicile</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-2">RÉPARATION & CHANGEMENT PARE-BRISE</h2>
            <p className="text-zinc-400">Service vitrage mobile à domicile ou au bureau • Agréé assurances</p>
          </div>

          {/* Avantage franchise */}
          <div className="mb-10 bg-gradient-to-r from-cyan-900/30 via-cyan-600/20 to-cyan-900/30 rounded-xl p-6 border border-cyan-500/30">
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left">
              <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center border-2 border-cyan-500/50">
                <Gift className="text-cyan-400" size={32} />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  🎁 Franchise offerte jusqu'à <span className="text-cyan-400">130€</span> sur tout remplacement de pare-brise !
                </h3>
                <p className="text-zinc-300 text-lg">
                  <strong className="text-cyan-400">Zéro stress :</strong> Nous prenons en charge l'intégralité de vos démarches administratives auprès de votre assureur.
                </p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES_VITRAGE.map((service, idx) => (
              <Card 
                key={service.id}
                className="bg-zinc-900/50 border-zinc-800 overflow-hidden card-hover"
                data-testid={`vitrage-${service.id}`}
              >
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-full bg-cyan-500/10 flex items-center justify-center mb-4">
                    <service.icon className="text-cyan-400" size={28} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{service.name}</h3>
                  <p className="text-zinc-400 text-sm mb-4">{service.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-red-500 font-bold">{service.price}</span>
                    <Link to="/devis">
                      <Button size="sm" variant="outline" className="border-cyan-700 text-cyan-400 hover:bg-cyan-500/10">
                        Devis
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-white mb-1">Agréé toutes assurances</h3>
                <p className="text-zinc-400">Prise en charge directe avec votre assurance • Aucune avance de frais</p>
              </div>
              <Link to="/contact">
                <Button className="bg-cyan-600 hover:bg-cyan-700">
                  <Phone size={18} className="mr-2" />
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="py-16 bg-zinc-900/30 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-2">ZONE D'INTERVENTION</h2>
            <p className="text-zinc-400">Nous intervenons sur Paris et toute l'Île-de-France</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <MapPin className="text-red-500" size={24} />
                  Paris & Petite Couronne
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-zinc-400">• Paris (75)</span>
                  <span className="text-zinc-400">• Hauts-de-Seine (92)</span>
                  <span className="text-zinc-400">• Seine-Saint-Denis (93)</span>
                  <span className="text-zinc-400">• Val-de-Marne (94)</span>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <MapPin className="text-orange-400" size={24} />
                  Grande Couronne
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-zinc-400">• Yvelines (78)</span>
                  <span className="text-zinc-400">• Essonne (91)</span>
                  <span className="text-zinc-400">• Val-d'Oise (95)</span>
                  <span className="text-zinc-400">• Seine-et-Marne (77)</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-red-900/20 via-red-600/10 to-red-900/20 border-t border-red-500/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">BESOIN D'UNE INTERVENTION ?</h2>
          <p className="text-zinc-400 mb-8">
            Contactez-nous pour planifier une intervention à domicile ou au bureau
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:0805032032">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 btn-glow">
                <Phone size={20} className="mr-2" />
                0805 032 032
              </Button>
            </a>
            <Link to="/devis">
              <Button size="lg" variant="outline" className="border-zinc-600 text-white hover:bg-zinc-800">
                Demander un devis
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Section paiement inline */}
      {paymentData && (
        <section ref={paymentSectionRef} className="py-12 bg-gradient-to-b from-zinc-900 to-[#09090B] border-y-2 border-red-600/30" data-testid="mobilite-payment-section">
          <div className="max-w-2xl mx-auto px-4">
            {paymentStatus === 'success' ? (
              <Card className="bg-zinc-900/50 border-green-500/50">
                <CardContent className="p-8 text-center">
                  <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
                  <h2 className="text-2xl font-bold text-white mb-2">
                    {paymentData.formToken ? 'Abonnement active !' : 'Demande enregistree !'}
                  </h2>
                  <p className="text-zinc-400 mb-6">
                    {paymentData.formToken
                      ? `Votre carte ${paymentData.planName} est desormais active.`
                      : `Votre demande pour ${paymentData.planName} a bien ete enregistree. Le paiement (${Number(paymentData.price).toFixed(2)} EUR) se fera en magasin.`}
                  </p>
                  <Button onClick={() => navigate('/mon-compte')} className="bg-red-600 hover:bg-red-700 w-full">
                    Voir mon espace
                  </Button>
                </CardContent>
              </Card>
            ) : paymentStatus === 'error' ? (
              <Card className="bg-zinc-900/50 border-red-500/50">
                <CardContent className="p-8 text-center">
                  <X className="mx-auto text-red-500 mb-4" size={64} />
                  <h2 className="text-2xl font-bold text-white mb-2">Échec du paiement</h2>
                  <p className="text-zinc-400 mb-6">Veuillez réessayer.</p>
                  <Button onClick={cancelPayment} variant="outline" className="border-zinc-700 w-full">
                    Retour
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-3xl font-bold text-white flex items-center gap-3">
                    <CreditCard className="text-red-500" size={32} /> PAIEMENT
                  </h2>
                  <Button onClick={cancelPayment} variant="ghost" className="text-zinc-400 hover:text-white">
                    Annuler
                  </Button>
                </div>
                <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-lg text-white font-bold">{paymentData.planName}</p>
                        <p className="text-sm text-zinc-400">Abonnement</p>
                      </div>
                      <p className="text-3xl text-red-500 font-bold">{Number(paymentData.price).toFixed(2)} €</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardContent className="p-6">
                    <SystemPayForm
                      formToken={paymentData.formToken}
                      amount={Number(paymentData.price || 0)}
                      onPaid={handlePaid}
                      onFailed={handleFailed}
                      testId="systempay-mobi-form"
                    />
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
